import { CONSTANTS__client } from '@/constants/CONSTANTS__client';
import { CONSTANTS_TIME__client } from '@/constants/CONSTANTS_TIME__client';
import { use_effect } from '@/hooks/_base/use_effect';
import { use_slice_workspace } from '@/hooks/store/use_slices';
import { set_timeout } from '@/utils/utils_client';
import { DTO__number_range } from '@shared/dto/generation/settings/DTO__number_range';
import { Fragment, useMemo, useRef, useState } from 'react';

interface Props {
  min: number;
  max: number;
  step: number;
  set_show_temporary_marks: (value: boolean) => void;
  show_temporary_marks: boolean;
}

export function SliderMarksTemporary({ min, max, step, set_show_temporary_marks, show_temporary_marks }: Props) {
  const ref_is_first_render = useRef(true);

  const { workspace_current } = use_slice_workspace(['workspace_current']);

  const [temporary_marks, set_temporary_marks] = useState<number[]>([]);

  const ref_step_cached = useRef(step);
  const ref_workspace_current_id = useRef('');

  /*
   * EFFECTS
   */
  use_effect(() => {
    if (ref_is_first_render.current) return void (ref_is_first_render.current = false);
    if (ref_step_cached.current === step) return; //step is not changed
    if (ref_workspace_current_id.current !== workspace_current._id) return void (ref_workspace_current_id.current = workspace_current._id); // switch workspaces

    ref_step_cached.current = step;

    let marks_array: number[] = [];

    for (let mark = min; mark <= max; mark += step) {
      marks_array.push(mark);
    }

    set_temporary_marks(marks_array);
    set_show_temporary_marks(true);

    let timeout_id = set_timeout(() => {
      set_temporary_marks([]);
      set_show_temporary_marks(false);
    }, CONSTANTS_TIME__client.TIMEOUT_ANIMATION_SLIDER_TEMPORARY_MARKS_DELAY);

    return () => {
      clearTimeout(timeout_id);
      set_temporary_marks([]);
      set_show_temporary_marks(false);
    };
  }, [step, min, max, workspace_current._id]);

  /*
   * RENDERS
   */

  function render_temporary_mark(mark: number, index: number) {
    let out = <Fragment key={mark} />;

    let is_last = index === temporary_marks.length - 1; // not show last mark
    let is_last_minus_one = index === temporary_marks.length - 2; // not show before the last

    if (temporary_marks.length >= CONSTANTS__client.SLIDER_VISIBLE_TEMPORARY_MARKS_COUNT_MAX && (is_last_minus_one || is_last)) return out;

    if (index === 0 || (temporary_marks.length >= CONSTANTS__client.SLIDER_VISIBLE_TEMPORARY_MARKS_COUNT_MIN && is_last)) return out;

    const percent = new DTO__number_range(min, max).value_to_percentage(mark);

    return <div key={mark} className="absolute bg-component-item-border w-[0.5px] h-full" style={{ left: `${percent}%` }} />;
  }

  const rendered_temporary_marks = useMemo(() => {
    if (temporary_marks.is_empty()) return null;

    return (
      <div className="absolute top-0 left-0 w-full h-full pointer-events-none transition-all duration-500" style={{ opacity: show_temporary_marks ? 1 : 0 }}>
        {temporary_marks.map(render_temporary_mark)}
      </div>
    );
  }, [temporary_marks, show_temporary_marks, min, max]);

  return rendered_temporary_marks;
}
